// Lib
import { FC, useEffect, useState } from "react";
import { CollapseProps } from "antd";
// Api
import { useGetCustomerMenuQuery } from "rtkQuery/query/customersAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Types
import {
  ComboGroupSubItem,
  CustomerMenuAddonModifier,
  CustomerMenuProduct,
} from "types/customers";
import {
  AddonsModifiers,
  CompleteProductData,
  MenuStatuses,
} from "../../types";
// Theme
// Constants
import { rtkQueryParams } from "consts";
// Helpers
import { errorHandler } from "utils/errorHandler";
import { getMenuStatuses, getSubtotal } from "../../helpers";
import {
  getComboGroupsItemsNumberTitle,
  getProductInputPrice,
  getProductItemPrice,
} from "./helpers";
// Icons
import { CurrencyIcon } from "icons";
// Components
import { Modal } from "components";
import {
  Input,
  InputQuantity,
  SearchProductsSelect,
  TextArea,
} from "components/Form";
import { Addons, Combos, Modifiers } from "./components";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";
import { Typography } from "styled/Typography";
import { InputsGridContainer, StyledCollapse, Title } from "./styled";

interface AddItemProps {
  customerId: string | undefined;
  locationId: string;
  itemData: boolean | CompleteProductData;
  setMenuId: (id: string) => void;
  setMenuStatuses: (data: MenuStatuses) => void;
  onAddItem: (data: CompleteProductData) => void;
  onClose: () => void;
}

export const AddItem: FC<AddItemProps> = ({
  customerId,
  locationId,
  itemData,
  setMenuId,
  setMenuStatuses,
  onAddItem,
  onClose,
}) => {
  const { openNotification } = useNotification();

  const { isMobile } = useViewport();

  const isEdit = !!itemData && typeof itemData !== "boolean";

  const [products, setProducts] = useState<CustomerMenuProduct[]>([]);

  const { data, error } = useGetCustomerMenuQuery(
    { id: customerId, query: { locationId } },
    { ...rtkQueryParams, skip: !customerId || !locationId },
  );

  useEffect(() => {
    if (error) {
      errorHandler({ error, openNotification });
      setProducts([]);
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      const products = data.categories.reduce(
        (acc: CustomerMenuProduct[], el) => {
          return acc.concat(el.products);
        },
        [],
      );

      const statuses = getMenuStatuses(data);

      setMenuId(data.id);
      setProducts(products);
      setMenuStatuses(statuses);
    }
  }, [data]);

  const [quantity, setQuantity] = useState(1);
  const [remark, setRemark] = useState("");

  const [selectedProduct, setSelectedProduct] =
    useState<CustomerMenuProduct | null>(null);

  const [selectedModifiers, setSelectedModifiers] = useState<AddonsModifiers>({
    addon: [],
    modifier: [],
  });

  const [selectedCombos, setSelectedCombos] = useState<Record<
    string,
    ComboGroupSubItem[]
  > | null>(null);

  useEffect(() => {
    if (!itemData) {
      setQuantity(1);
      setRemark("");
      setSelectedProduct(null);
      setSelectedModifiers({
        addon: [],
        modifier: [],
      });
      setSelectedCombos(null);
      return;
    }

    if (typeof itemData !== "boolean") {
      setSelectedModifiers(itemData.addonsModifiers);
      setQuantity(itemData.quantity);
      setRemark(itemData?.remark || "");
      setSelectedProduct(itemData.product);
      setSelectedCombos(itemData.combos || null);
    }
  }, [itemData]);

  const onClear = () => {
    setQuantity(1);
    setRemark("");
    setSelectedProduct(null);
    setSelectedCombos(null);
    setSelectedModifiers({
      addon: [],
      modifier: [],
    });
  };

  const onCheck = (
    addonModifier: CustomerMenuAddonModifier,
    value: boolean,
  ) => {
    if (value) {
      setSelectedModifiers(prev => {
        if (prev[addonModifier.type]?.find(el => el.id === addonModifier.id)) {
          return prev;
        }
        return {
          ...prev,
          [addonModifier.type]: prev[addonModifier.type].concat({
            ...addonModifier,
            quantity: 1,
          }),
        };
      });

      return;
    }

    setSelectedModifiers(prev => {
      if (prev[addonModifier.type]?.find(el => el.id === addonModifier.id)) {
        return {
          ...prev,
          [addonModifier.type]: prev[addonModifier.type].filter(
            el => el.id !== addonModifier.id,
          ),
        };
      }
      return prev;
    });
  };

  const onComboCheck = ({
    comboGroupId,
    subItem,
    value,
    min,
    max,
  }: {
    comboGroupId: string;
    subItem: ComboGroupSubItem;
    value: boolean;
    min?: number;
    max?: number;
  }) => {
    if (min === 1 && max === 1) {
      setSelectedCombos(prev => ({
        ...prev,
        [comboGroupId]: [subItem],
      }));
      return;
    }

    setSelectedCombos(prev => {
      if (!prev) return { [comboGroupId]: [subItem] };

      const currentCombo = prev[comboGroupId] || [];

      if (value) {
        if (currentCombo.find(el => el.id === subItem.id)) return prev;

        if (!!max && currentCombo.length >= max) {
          return prev; // Do not allow adding more items than max
        }

        return {
          ...prev,
          [comboGroupId]: currentCombo.concat(subItem),
        };
      }

      if (currentCombo.find(el => el.id === subItem.id)) {
        return {
          ...prev,
          [comboGroupId]: currentCombo.filter(el => el.id !== subItem.id),
        };
      }

      return prev;
    });
  };

  const handleAddItem = () => {
    const uniqId =
      !!itemData && typeof itemData !== "boolean"
        ? itemData?.uniqId
        : undefined;

    onAddItem({
      uniqId,
      product: selectedProduct,
      addonsModifiers: selectedModifiers,
      quantity,
      combos: selectedCombos,
      remark,
    });
  };

  const setModifierQuantity = (
    addonModifier: CustomerMenuAddonModifier,
    quantity: number,
  ) => {
    if (quantity) {
      setSelectedModifiers(prev => {
        const addonModifierItem = prev[addonModifier.type]?.find(
          el => el.id === addonModifier.id,
        );

        if (addonModifierItem) {
          return {
            ...prev,
            [addonModifier.type]: prev[addonModifier.type]
              ?.filter(el => el.id !== addonModifier.id)
              .concat({ ...addonModifier, quantity }),
          };
        }
      });

      return;
    }
  };

  const onProductSelect = (product: CustomerMenuProduct | null) => {
    setSelectedProduct(product || null);
    setSelectedModifiers({
      addon: [],
      modifier: [],
    });
    setQuantity(1);
    const requridedCombos = product?.comboGroups?.filter(
      group => group.min === 1 && group.max === 1,
    );

    const minimumPriceComboItems = requridedCombos?.reduce<
      Record<string, ComboGroupSubItem[]>
    >((acc, group) => {
      const minItem = group.subItems.reduce<ComboGroupSubItem | null>(
        (min, item) => {
          if (!min || item.fmcCentsPrice < min.fmcCentsPrice) return item;
          return min;
        },
        null,
      );

      if (minItem) {
        acc[group.id] = [minItem];
      }
      return acc;
    }, {});

    setSelectedCombos(minimumPriceComboItems || null);
  };

  const options =
    products?.map(i => ({
      label: i.name,
      value: i.id,
      subdata: i,
      disabled: i.isSnoozed,
    })) || [];

  const { subTotalCoins, subTotalFiat } = getSubtotal({
    product: selectedProduct,
    addonsModifiers: selectedModifiers,
    combos: selectedCombos,
    quantity,
  });

  const comboGroupsMinValues = selectedProduct?.comboGroups?.reduce<
    Record<string, number>
  >((acc, group) => {
    if (!group.min) return acc;

    acc[group.id] = group.min;

    return acc;
  }, {});

  const isAllCombosSelected = Object.entries(comboGroupsMinValues || {}).every(
    ([key, value]) => selectedCombos?.[key]?.length === value,
  );

  const items: CollapseProps["items"] = [
    ...(selectedProduct?.comboGroups?.length
      ? selectedProduct?.comboGroups?.map(comboGroup => ({
          key: comboGroup.id,
          label: (
            <FlexContainer $gap={8} $align="center" $justify="space-between">
              <Title>
                {comboGroup?.name?.en || comboGroup?.name?.ar || "Group"}
              </Title>

              <Typography.DescriptionThin>
                {getComboGroupsItemsNumberTitle(comboGroup.min, comboGroup.max)}
              </Typography.DescriptionThin>
            </FlexContainer>
          ),
          children: (
            <Combos
              comboGroup={comboGroup}
              selectedCombos={selectedCombos}
              onComboCheck={onComboCheck}
            />
          ),
        })) || []
      : []),
    ...(selectedProduct?.addons?.length
      ? [
          {
            key: `${selectedProduct.id}-addons`,
            label: <Title>Addons</Title>,
            children: (
              <Addons
                selectedProduct={selectedProduct}
                selectedModifiers={selectedModifiers}
                onCheck={onCheck}
                setModifierQuantity={setModifierQuantity}
              />
            ),
          },
        ]
      : []),
    ...(selectedProduct?.modifiers?.length
      ? [
          {
            key: `${selectedProduct.id}-modifiers`,
            label: <Title>Modifiers</Title>,
            children: (
              <Modifiers
                selectedProduct={selectedProduct}
                selectedModifiers={selectedModifiers}
                onCheck={onCheck}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Modal width={560} title="Add Item" open={!!itemData} onClose={onClose}>
      <FlexContainer $fullwidth $column $gap={24}>
        <FlexContainer $column $gap={16}>
          <SearchProductsSelect
            required
            label="Food name"
            placeholder="Search food name"
            disabled={isEdit}
            value={selectedProduct?.id}
            options={options}
            optionRender={option => (
              <FlexContainer
                $align="center"
                $justify="space-between"
                $padding="8px 0"
              >
                <Typography.Title style={{ whiteSpace: "pre-wrap" }}>
                  {option.label}
                </Typography.Title>

                <FlexContainer $gap={4} $minWidth="160px" $justify="flex-end">
                  <CurrencyIcon width="24" height="24" />

                  <Typography.Title>
                    {getProductItemPrice(option.data.subdata)}
                  </Typography.Title>
                </FlexContainer>
              </FlexContainer>
            )}
            onSelect={(_value, option) => onProductSelect(option.subdata)}
            onClear={onClear}
          />

          <InputsGridContainer>
            <Input
              prefix={<CurrencyIcon />}
              label="Price"
              disabled
              value={getProductInputPrice(selectedProduct, selectedCombos)}
            />

            <InputQuantity
              label="Quantity"
              value={quantity}
              onChange={(value: number) => setQuantity(value)}
            />
          </InputsGridContainer>
        </FlexContainer>

        <TextArea
          label="Note"
          placeholder="Enter the Note"
          value={remark}
          onChange={e => setRemark(e.target.value)}
        />

        <StyledCollapse ghost items={items} />
      </FlexContainer>

      <FlexContainer
        $fullwidth
        $align="center"
        $justify="space-between"
        $padding="24px 0 0 0"
        $column={isMobile}
        $gap={isMobile && 16}
      >
        <FlexContainer $column $gap={8} $fullwidth={isMobile}>
          <Typography.Title>Subtotal</Typography.Title>

          <FlexContainer $gap={4}>
            <CurrencyIcon width="24" height="24" />

            <Title>
              {subTotalCoins} | KD {subTotalFiat}
            </Title>
          </FlexContainer>
        </FlexContainer>

        <FlexContainer $gap={8} $column={isMobile} $fullwidth={isMobile}>
          <Button.Heading onClick={onClose}>Cancel</Button.Heading>

          <Button.Heading
            type="primary"
            disabled={!selectedProduct || !isAllCombosSelected}
            onClick={handleAddItem}
          >
            {isEdit ? "Save" : "Add"} Item
          </Button.Heading>
        </FlexContainer>
      </FlexContainer>
    </Modal>
  );
};
